import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LangLink } from "@/components/LangLink";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Globe, Warehouse, ShieldCheck } from "lucide-react";
import { Seo } from "@/components/Seo";
import { ProductCard } from "@/components/ProductCard";
import { SmartImage } from "@/components/SmartImage";
import { EditorialHero } from "@/components/hero/EditorialHero";
import { AeoAnswerBlock } from "@/components/aeo/AeoAnswerBlock";
import { AeoFaq } from "@/components/aeo/AeoFaq";
import { Reveal, ParallaxBand } from "@/components/Reveal";
import { getFeatured, getCategories, CATEGORY_NAV, BRAND_NAV } from "@/lib/api";
import { loadCmsOverrides } from "@/lib/cms";
import { SITE_URL } from "@/lib/siteConfig";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const ED_GROOM = "/kentucky/life2.jpg";
const ED_BITS = "/kentucky/life3.jpg";
const ease = [0.22, 1, 0.36, 1];

const VALUES = [
  { icon: Globe, h: "Shipped worldwide", p: "Express, tracked delivery to riders across the globe." },
  { icon: Warehouse, h: "Ireland & EU warehouses", p: "In stock locally and dispatched fast, with free delivery over \u20ac100 in Ireland." },
  { icon: ShieldCheck, h: "Official reseller", p: "Authentic Kentucky Horsewear with full manufacturer warranty." },
];

const AEO_ANSWERS = [
  {
    q: "Is Boomerang.ie an official Kentucky Horsewear reseller?",
    a: "Yes. Boomerang.ie Horse & Country is an official Kentucky Horsewear reseller, offering the complete authentic range of rugs, saddle pads, fly veils, boots, bridles, grooming and the Kentucky Dog collection, all with full manufacturer warranty.",
    d: "As an authorised stockist, every item is genuine Kentucky Horsewear sourced directly through official channels, backed by warranty and expert after-sales support.",
  },
  {
    q: "Does Boomerang.ie ship Kentucky Horsewear worldwide?",
    a: "Yes. We dispatch Kentucky Horsewear worldwide with express tracked couriers (DPD, DHL, UPS and An Post) from our Ireland and EU warehouses. Irish orders over \u20ac100 ship free.",
    d: "Most EU destinations receive orders within 2 to 4 working days. Worldwide express delivery times and duties are shown at checkout.",
  },
  {
    q: "Can I pay in instalments and earn loyalty points?",
    a: "Yes. Klarna and Humm let you split the cost at checkout, and you earn Bon loyalty points on every order to redeem against future Kentucky purchases.",
  },
];

const FAQS = [
  { q: "How do I choose the right Kentucky rug or saddle pad size?", a: "Each Kentucky product page includes detailed sizing, and our Size Guide converts between centimetres, feet and standard horse sizes for rugs, boots and bridles." },
  { q: "What is Kentucky Horsewear known for?", a: "Founded in Belgium, Kentucky Horsewear is renowned for anatomic, welfare-first design and technical fabrics across turnout rugs, saddle pads, leg protection, bridles and grooming." },
  { q: "Do you offer free shipping?", a: "Yes. Irish orders over \u20ac100 ship free, with express worldwide delivery available across the EU and internationally." },
  { q: "Can I return Kentucky products?", a: "Yes. Unused items in original condition can be returned within our stated returns window; see the Shipping & Returns page for full details." },
];

export default function Home() {
  const { lang, tx } = useI18n();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cmsMap, setCmsMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getFeatured(), getCategories(lang)])
      .then(([f, c]) => { setProducts(f.products || []); setCategories(c.categories || []); })
      .finally(() => setLoading(false));
  }, [lang]);

  // Load admin CMS image overrides separately + slightly deferred so it never
  // blocks product/category rendering (requests fired synchronously during the
  // initial mount can stall in this environment).
  useEffect(() => {
    let mounted = true;
    const id = setTimeout(() => {
      loadCmsOverrides().then((o) => { if (mounted) setCmsMap(o || {}); });
    }, 250);
    return () => { mounted = false; clearTimeout(id); };
  }, []);

  const cms = (slot) => cmsMap?.[slot]?.url || null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: "Boomerang.ie Horse & Country",
        url: SITE_URL,
        logo: `${SITE_URL}/brands/kentucky.png`,
        sameAs: ["https://boomerang.ie"],
        areaServed: ["IE", "GB", "US", "CA", "BE", "DE", "NL", "FI", "Worldwide"],
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        name: "Kentucky Horsewear Ireland | Boomerang.ie",
        url: SITE_URL,
        publisher: { "@id": `${SITE_URL}/#organization` },
        potentialAction: {
          "@type": "SearchAction",
          target: { "@type": "EntryPoint", urlTemplate: `${SITE_URL}/search?q={search_term_string}` },
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@type": "Store",
        name: "Kentucky Horsewear at Boomerang.ie",
        url: SITE_URL,
        description: "Official reseller of Kentucky Horsewear, Dy'on, Velari and Grooming Deluxe. Express worldwide shipping.",
        areaServed: ["IE", "EU", "US", "CA", "Worldwide"],
        priceRange: "\u20ac\u20ac",
      },
    ],
  };

  const cats = categories.length ? categories : CATEGORY_NAV;

  return (
    <>
      <Seo title="Kentucky Horsewear, Express Worldwide" description="Shop the full Kentucky Horsewear range at Boomerang.ie, an official reseller. Rugs, saddle pads, fly veils, boots, bridles, grooming, rider apparel and the Kentucky Dog collection. Express worldwide shipping; free over \u20ac100 in Ireland." path="/" image="/kentucky/life5.jpg" jsonLd={jsonLd} />

      {/* HERO */}
      <EditorialHero heroImage={cms("hero:main")} />

      {/* VALUE PROPS */}
      <section className="container-site py-12 lg:py-16">
        <div className="grid gap-4 sm:grid-cols-3">
          {VALUES.map((v, i) => (
            <Reveal key={v.h} delay={i * 0.06}>
              <div className="flex h-full min-h-[160px] flex-col justify-between rounded-2xl border border-foreground/10 bg-card p-7">
                <v.icon className="h-7 w-7 text-[hsl(var(--primary))]" />
                <div>
                  <p className="font-display text-2xl">{tx(v.h)}</p>
                  <p className="mt-1.5 text-sm text-muted-foreground">{tx(v.p)}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CATEGORY SHOWCASE \u2014 atelier bento */}
      <section data-testid="category-showcase-section" className="container-site py-10 lg:py-14">
        <div className="mb-8 flex items-end justify-between gap-4">
          <Reveal><h2 className="font-display text-3xl sm:text-5xl">{tx("Shop by Category")}</h2></Reveal>
          <LangLink to="/brand/kentucky" className="hidden items-center gap-1.5 text-sm font-medium uppercase tracking-[0.12em] text-foreground/70 hover:text-foreground sm:inline-flex">
            {tx("View all")} <ArrowUpRight className="h-4 w-4" />
          </LangLink>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cats.map((c, i) => {
            const catImg = cms(`category:${c.slug}`) || c.sample_image || c.hero;
            return (
            <motion.div key={c.slug} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-6%" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.05, ease }}>
              <LangLink to={`/collection/${c.slug}`} data-testid="category-tile"
                className="group relative block aspect-[4/3] overflow-hidden rounded-2xl border border-foreground/10 bg-white">
                {catImg ? (
                  <SmartImage src={catImg} alt={c.name}
                    className="absolute inset-0 h-full w-full"
                    imgClassName="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                ) : (
                  <div className="absolute inset-0 bg-secondary" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--brand-navy))]/90 via-[hsl(var(--brand-navy))]/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-5">
                  <div>
                    <span className="font-display text-2xl text-white">{c.name}</span>
                    {c.count > 0 && <p className="text-xs text-white/70">{c.count} {tx("products")}</p>}
                  </div>
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition-transform group-hover:translate-x-0.5">
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
              </LangLink>
            </motion.div>
            );
          })}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="container-site py-12 lg:py-16">
        <div className="flex items-end justify-between gap-4">
          <Reveal><h2 className="font-display text-3xl sm:text-5xl">{tx("Featured Kentucky")}</h2></Reveal>
          <LangLink to="/brand/kentucky" className="inline-flex items-center gap-1.5 text-sm font-medium uppercase tracking-[0.12em] text-foreground/70 hover:text-foreground">{tx("View all")} <ArrowUpRight className="h-4 w-4" /></LangLink>
        </div>
        {loading ? (
          <div className="mt-8 grid grid-cols-2 gap-x-5 gap-y-9 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (<div key={i}><Skeleton className="aspect-[4/5] w-full" /><Skeleton className="mt-3 h-4 w-2/3" /></div>))}
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-2 gap-x-5 gap-y-9 md:grid-cols-3 lg:grid-cols-4" data-testid="product-grid">
            {products.map((p, i) => <ProductCard key={p.handle} product={p} index={i} />)}
          </div>
        )}
      </section>

      {/* AEO ANSWERS \u2014 navy section, answer-first cards for AI engines */}
      <section data-testid="aeo-answers-section" className="section-navy grain relative">
        <div className="container-site py-16 lg:py-24">
          <Reveal>
            <p className="eyebrow text-[hsl(var(--primary))]">{tx("Ask anything")}</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl leading-tight text-white sm:text-5xl">{tx("Clear answers to the questions riders ask most")}</h2>
          </Reveal>
          <div className="mt-10 grid gap-4 lg:grid-cols-3">
            {AEO_ANSWERS.map((a, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <AeoAnswerBlock question={a.q} answer={a.a} details={a.d ? tx(a.d) : null} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CRAFTSMANSHIP PARALLAX BAND */}
      <ParallaxBand image="/kentucky/hero-rider.jpg" overlay="bg-gradient-to-r from-[hsl(var(--brand-navy))] via-[hsl(var(--brand-navy))]/90 to-[hsl(var(--brand-navy))]/60" className="my-0">
        <div className="container-site py-24 text-white lg:py-32">
          <Reveal>
            <p className="eyebrow text-[hsl(var(--primary))]">{tx("Craftsmanship")}</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl leading-tight sm:text-5xl">{tx("Built for the yard. Refined for the arena.")}</h2>
            <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-white/80">{tx("Anatomical fits, breathable technical fabrics and welfare-first detailing. Every Kentucky piece is engineered to protect and perform, season after season.")}</p>
            <Button asChild size="lg" className="mt-7"><LangLink to="/brand/kentucky">{tx("Explore the Range")} <ArrowRight className="ml-2 h-4 w-4" /></LangLink></Button>
          </Reveal>
        </div>
      </ParallaxBand>

      {/* EDITORIAL SPLIT BANDS */}
      <section className="container-site grid gap-4 py-12 lg:grid-cols-2 lg:py-16">
        {[
          { img: ED_GROOM, eyebrow: "Grooming Deluxe", h: "A show-ring shine, every day", to: "/collection/grooming", cta: "Shop Grooming" },
          { img: ED_BITS, eyebrow: "Anatomic by design", h: "Bridles & tack, welfare-first", to: "/collection/bridles-tack", cta: "Shop Bridles & Tack" },
        ].map((b, i) => (
          <Reveal key={b.to} delay={i * 0.06}>
            <div className="group relative h-full min-h-[320px] overflow-hidden rounded-2xl">
              <img src={b.img} alt={b.h} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--brand-navy))]/90 to-[hsl(var(--brand-navy))]/10" />
              <div className="absolute inset-x-0 bottom-0 p-7">
                <p className="eyebrow text-[hsl(var(--primary))]">{tx(b.eyebrow)}</p>
                <h3 className="mt-2 font-display text-2xl text-white sm:text-3xl">{tx(b.h)}</h3>
                <Button asChild className="mt-4"><LangLink to={b.to}>{tx(b.cta)}</LangLink></Button>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      {/* BRANDS \u2014 maisons */}
      <section className="container-site py-14 lg:py-20">
        <Reveal><h2 className="font-display text-3xl sm:text-5xl">{tx("Our Maisons")}</h2></Reveal>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {BRAND_NAV.map((b, i) => {
            const brandImg = cms(`brand:${b.slug}`);
            return (
            <motion.div key={b.slug} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.05, ease }}>
              <LangLink to={`/brand/${b.slug}`} data-testid="brand-tile"
                className="group relative flex h-full min-h-[180px] flex-col items-center justify-center gap-5 overflow-hidden rounded-2xl border border-foreground/10 bg-card p-8 text-center transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(8,8,64,0.10)]">
                {brandImg && (
                  <>
                    <SmartImage src={brandImg} alt={b.name}
                      className="absolute inset-0 h-full w-full"
                      imgClassName="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--brand-navy))]/85 via-[hsl(var(--brand-navy))]/35 to-[hsl(var(--brand-navy))]/15" />
                  </>
                )}
                <div className="relative z-10 flex flex-col items-center gap-5">
                  {b.logo
                    ? <img src={b.logo} alt={b.name} className={`h-8 w-auto object-contain transition-opacity ${brandImg ? "opacity-0" : "opacity-70 group-hover:opacity-100"}`} />
                    : <p className={`font-display text-xl ${brandImg ? "text-white" : ""}`}>{b.name}</p>}
                  {brandImg && <p className="font-display text-2xl text-white">{b.name}</p>}
                  <span className={`inline-flex items-center gap-1.5 text-[12px] font-medium uppercase tracking-[0.14em] ${brandImg ? "text-white/80" : "text-foreground/70"}`}>{tx("Explore")} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
                </div>
              </LangLink>
            </motion.div>
            );
          })}
        </div>
      </section>

      {/* SEO CONTENT + FAQ */}
      <section className="border-t border-foreground/10 bg-secondary/30">
        <div className="container-site grid gap-12 py-16 lg:grid-cols-2 lg:py-20">
          <article data-testid="seo-content-block" className="prose prose-neutral max-w-none prose-headings:font-display">
            <h2>{tx("Kentucky Horsewear, shipped worldwide")}</h2>
            <p>{tx("Founded in Belgium, Kentucky Horsewear is one of the most recognised names in premium equestrian equipment, combining technical innovation with refined, durable design. As an official reseller, Boomerang.ie brings the complete Kentucky collection to riders worldwide, from celebrated turnout and stable rugs to saddle pads, fly veils, leg protection, anatomic bridles, grooming, rider apparel and the luxury Kentucky Dog range.")}</p>
            <p>{tx("Stock is held in our Ireland and EU warehouses and dispatched with express worldwide delivery, free on Irish orders over \u20ac100. Alongside Kentucky we curate Dy'on handcrafted leatherwork, Velari luxury and Grooming Deluxe professional tools.")}</p>
            <p className="not-prose mt-4 flex flex-wrap gap-2">
              {[{ to: "/collection/horse-rugs", n: "Horse Rugs" }, { to: "/collection/saddle-pads", n: "Saddle Pads" }, { to: "/collection/dog", n: "Dog" }, { to: "/collection/grooming", n: "Grooming" }, { to: "/size-guide", n: "Size Guide" }].map((l) => (
                <LangLink key={l.to} to={l.to} className="rounded-full border border-foreground/15 bg-card px-3.5 py-1.5 text-sm font-medium no-underline hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]">{tx(l.n)}</LangLink>
              ))}
            </p>
          </article>
          <div data-testid="seo-faq-accordion">
            <AeoFaq faqs={FAQS} title="Frequently asked questions" />
          </div>
        </div>
      </section>
    </>
  );
}
